/**
 * Client-side scan of project rows' `acf_groups_json` for Settings → Tables catalog merge.
 */

import axios from "axios";
import { getApiBase } from "../apiBase";
import { buildPluginsHeader } from "./registry";

const AUTH_TOKEN_KEY = "projectBoardAuthToken";

function catalogAuthHeaders() {
  const h = { ...buildPluginsHeader() };
  try {
    const t = localStorage.getItem(AUTH_TOKEN_KEY);
    if (t) h.Authorization = `Bearer ${t}`;
  } catch {
    /* ignore */
  }
  return h;
}

/** Same payload as the main grid (`GET /projects`). */
export async function fetchAllProjectRowsForCatalog() {
  const { data } = await axios.get(`${getApiBase()}/projects`, { headers: catalogAuthHeaders() });
  return Array.isArray(data) ? data : [];
}

export const CATALOG_FIELD_TYPES = [
  { id: "text", label: "Text" },
  { id: "number", label: "Number" },
  { id: "date", label: "Date" },
  { id: "boolean", label: "Boolean" },
  { id: "selection", label: "Selection (string list)" },
  { id: "json", label: "JSON / structured" },
  { id: "url", label: "URL" },
  { id: "file", label: "File / attachment" },
];

const VALID_FIELD_TYPES = new Set(CATALOG_FIELD_TYPES.map((t) => t.id));

const MAX_SAMPLES_PER_KEY = 40;
const MAX_DEPTH = 5;

function catalogNormalizeSegment(s) {
  return String(s || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

export function catalogSlugForAcfPath(acfGroupName, fieldPath) {
  const g = catalogNormalizeSegment(acfGroupName);
  const p = String(fieldPath || "")
    .trim()
    .toLowerCase()
    .replace(/\./g, "__")
    .replace(/[^a-z0-9_]+/g, "_")
    .replace(/^_+|_+$/g, "");
  if (!g || !p) return "";
  return `${g}__${p}`;
}

function catalogHumanizePath(path) {
  return String(path || "")
    .split(".")
    .map((p) => p.replace(/_/g, " "))
    .join(" → ");
}

function catalogIsIsoDateString(s) {
  if (typeof s !== "string") return false;
  const t = s.trim();
  if (t.length < 8 || t.length > 40) return false;
  const d = Date.parse(t);
  return Number.isFinite(d) && /^\d{4}-\d{2}-\d{2}/.test(t);
}

function catalogIsUrlString(s) {
  if (typeof s !== "string") return false;
  return /^https?:\/\//i.test(s.trim());
}

function catalogInferFieldType(samples) {
  if (!samples || samples.length === 0) return "text";
  const types = new Set();
  for (const v of samples) {
    if (v === null || v === undefined) types.add("null");
    else if (typeof v === "boolean") types.add("boolean");
    else if (typeof v === "number" && Number.isFinite(v)) types.add("number");
    else if (Array.isArray(v)) types.add("array");
    else if (typeof v === "object") types.add("object");
    else if (typeof v === "string") {
      if (catalogIsUrlString(v)) types.add("url");
      else if (catalogIsIsoDateString(v)) types.add("date");
      else types.add("string");
    } else types.add("other");
  }
  if (types.has("object")) return "json";
  if (types.has("array")) {
    const arrs = samples.filter(Array.isArray);
    const allStr = arrs.every((a) => a.length === 0 || a.every((x) => typeof x === "string"));
    return allStr ? "selection" : "json";
  }
  if (
    types.has("boolean") &&
    !types.has("number") &&
    !types.has("string") &&
    !types.has("url") &&
    !types.has("date")
  ) {
    return "boolean";
  }
  if (
    types.has("number") &&
    !types.has("string") &&
    !types.has("url") &&
    !types.has("date") &&
    !types.has("boolean")
  ) {
    return "number";
  }
  if (types.has("url") && ![...types].some((t) => t === "string" || t === "date")) return "url";
  if (
    types.has("date") &&
    samples.every((v) => typeof v !== "string" || catalogIsIsoDateString(v) || (typeof v === "string" && v.trim() === ""))
  ) {
    return "date";
  }
  return "text";
}

function catalogWalkGroupLeaves(acfGroupName, obj, prefix, depth, acc) {
  if (!obj || typeof obj !== "object" || depth > MAX_DEPTH) return;
  if (Array.isArray(obj)) return;
  for (const [k, v] of Object.entries(obj)) {
    const path = prefix ? `${prefix}.${k}` : k;
    if (v !== null && typeof v === "object" && !Array.isArray(v)) {
      catalogWalkGroupLeaves(acfGroupName, v, path, depth + 1, acc);
    } else {
      const key = `${acfGroupName}\0${path}`;
      if (!acc.has(key)) acc.set(key, { acfGroupName, fieldPath: path, samples: [] });
      const e = acc.get(key);
      if (e.samples.length < MAX_SAMPLES_PER_KEY) e.samples.push(v);
    }
  }
}

/**
 * @param {Array<{ acf_groups_json?: unknown }>} rows
 * @returns {{ projectCount: number, entries: Array<{ slug: string, label: string, acfGroupName: string, fieldPath: string, fieldType: string }> }}
 */
export function scanFieldCatalogFromProjectRows(rows) {
  const merged = new Map();
  for (const row of rows) {
    const ag = row?.acf_groups_json;
    if (!ag || typeof ag !== "object" || Array.isArray(ag)) continue;
    for (const [groupName, groupVal] of Object.entries(ag)) {
      if (!groupVal || typeof groupVal !== "object" || Array.isArray(groupVal)) continue;
      catalogWalkGroupLeaves(groupName, groupVal, "", 0, merged);
    }
  }
  const entries = [];
  for (const { acfGroupName, fieldPath, samples } of merged.values()) {
    const slug = catalogSlugForAcfPath(acfGroupName, fieldPath);
    if (!slug) continue;
    const inferred = catalogInferFieldType(samples);
    const fieldType = VALID_FIELD_TYPES.has(inferred) ? inferred : "text";
    entries.push({
      slug,
      label: `${catalogHumanizePath(fieldPath)} — ${acfGroupName}`,
      acfGroupName,
      fieldPath,
      fieldType,
    });
  }
  entries.sort((a, b) => {
    const g = a.acfGroupName.localeCompare(b.acfGroupName);
    if (g !== 0) return g;
    return a.fieldPath.localeCompare(b.fieldPath);
  });
  return { projectCount: rows.length, entries };
}

export function normalizeCatalogFieldType(t) {
  const s = String(t || "").trim();
  return VALID_FIELD_TYPES.has(s) ? s : "text";
}
